import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import {
  Label,
  mergeClasses,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  useFocusFinders,
} from '@fluentui/react-components';
import { useId } from '@fluentui/react-hooks';

import { I18n, SelectionFlatTreeProps } from '../types';
import { LoadingIndicator } from '../components/loading-indicator';
import { useInlineContainerStyles } from './inline-container-styles';
import { EntitySelectorSearchBoxProps, SearchBox } from '../components';
import { BrowseButton } from '../components/browse-button';

export interface InlineContainerProps {
  i18n: I18n;
  className?: string;
  selectionMode: SelectionFlatTreeProps['selectionMode'];
  label?: string;
  showBrowseButton?: boolean;
  popOverContentBefore?: React.ReactNode;
  popOverContentAfter?: React.ReactNode;
  searchboxProps?: EntitySelectorSearchBoxProps;
  customFilter?: (item: any, search: string) => boolean;
  onRenderItemContent?: SelectionFlatTreeProps['onRenderContent'];
  children?: any;
}

export const InlineContainer: React.FC<InlineContainerProps> = ({
  i18n,
  className,
  selectionMode,
  label,
  showBrowseButton,
  popOverContentBefore,
  popOverContentAfter,
  searchboxProps,
  customFilter,
  onRenderItemContent,
  children,
}) => {
  const classes = useInlineContainerStyles();
  const { findFirstFocusable } = useFocusFinders();

  const searchRef = useRef(null);
  const searchInputRef = useRef(null);
  const popOverRef = useRef(null);

  const searchboxLabelId = useId('searchbox-label');

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const onChange = useCallback((e, data) => {
    const trimmedValue = data.value.trim();
    if (trimmedValue) {
      setIsPopoverOpen(true);
    }
  }, []);

  const onSearchKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (['Enter', 'ArrowDown', 'ArrowUp'].includes(e.key)) {
      e.preventDefault();
      setIsPopoverOpen(true);

      setTimeout(() => findFirstFocusable(popOverRef.current)?.focus(), 0);
    } else if (e.key === ' ') {
      e.stopPropagation();
      setIsPopoverOpen(true);
    }
  }, [findFirstFocusable]);

  const onSearchKeyUp = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation();
  }, []);

  useEffect(() => {
    if (!isPopoverOpen) {
      searchInputRef.current?.focus();
    }
  }, [findFirstFocusable, isPopoverOpen]);

  return (
    <div className={mergeClasses('entity-selector-inline', classes.root, className)}>
      {label && (
      <Label
        id={searchboxLabelId}
        className={classes.label}
      >
        {label}
      </Label>
      )}
      <Popover
        open={isPopoverOpen}
        onOpenChange={() => setIsPopoverOpen(!isPopoverOpen)}
        trapFocus
        unstable_disableAutoFocus
        positioning={{
          target: searchRef.current,
          align: 'start',
          position: 'below',
          matchTargetSize: 'width',
        }}
      >
        <PopoverTrigger>
          <div
            ref={searchRef}
            className={classes.searchboxContainer}
          >
            <SearchBox
              {...searchboxProps}
              aria-labelledby={searchboxLabelId}
              inputRef={searchInputRef}
              className={classes.searchbox}
              contentAfter={showBrowseButton && <BrowseButton i18n={i18n} />}
              onChange={onChange}
              onKeyDown={onSearchKeyDown}
              onKeyUp={onSearchKeyUp}
            />
          </div>
        </PopoverTrigger>
        <PopoverSurface
          ref={popOverRef}
          className={mergeClasses('entities-collection-container', classes.popOverSurface)}
        >
          {popOverContentBefore}

          {React.Children.map(children, (child) => React.cloneElement(child, {
            i18n,
            customFilter,
            selectionMode,
            aggregateMode: 'all',
            onRenderItemContent,
            infiniteScroll: true,
          }))}
          <LoadingIndicator i18n={i18n} />
          {popOverContentAfter}
        </PopoverSurface>
      </Popover>
    </div>
  );
};
